import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

const ADMIN_TOKEN_KEY = 'admin_auth_token';
const ADMIN_USER_KEY = 'admin_user_info';

export interface AdminUserInfo {
  id: string;
  username: string;
  display_name: string;
  role: 'super_admin' | 'editor';
}

function getStoredUser(): AdminUserInfo | null {
  try {
    const raw = localStorage.getItem(ADMIN_USER_KEY);
    return raw ? (JSON.parse(raw) as AdminUserInfo) : null;
  } catch {
    return null;
  }
}

export function useAdminAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return !!localStorage.getItem(ADMIN_TOKEN_KEY);
  });

  const [currentUser, setCurrentUser] = useState<AdminUserInfo | null>(() => getStoredUser());

  const login = useCallback(async (username: string, password: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from('admin_users')
      .select('id, username, display_name, role, is_active, password_hash')
      .eq('username', username.trim())
      .maybeSingle();

    if (error || !data) return false;
    if (!data.is_active) return false;
    if (data.password_hash !== password) return false;

    const token = btoa(`${username}:${Date.now()}:${Math.random()}`);
    const userInfo: AdminUserInfo = {
      id: data.id,
      username: data.username,
      display_name: data.display_name,
      role: data.role,
    };

    localStorage.setItem(ADMIN_TOKEN_KEY, token);
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(userInfo));

    // update last_login_at
    await supabase
      .from('admin_users')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', data.id);

    // write login log
    await supabase.from('admin_operation_logs').insert({
      operator_username: userInfo.username,
      operator_display_name: userInfo.display_name,
      operator_role: userInfo.role,
      module: '系统',
      action: '登录',
      target_name: '',
      detail: '登录后台管理系统',
    });

    setIsAuthenticated(true);
    setCurrentUser(userInfo);
    return true;
  }, []);

  const logout = useCallback(async () => {
    const user = getStoredUser();
    if (user) {
      await supabase.from('admin_operation_logs').insert({
        operator_username: user.username,
        operator_display_name: user.display_name,
        operator_role: user.role,
        module: '系统',
        action: '退出',
        target_name: '',
        detail: '退出后台管理系统',
      });
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_USER_KEY);
    setIsAuthenticated(false);
    setCurrentUser(null);
  }, []);

  const checkAuth = useCallback((): boolean => {
    return !!localStorage.getItem(ADMIN_TOKEN_KEY);
  }, []);

  return { isAuthenticated, currentUser, login, logout, checkAuth };
}

export function isAdminLoggedIn(): boolean {
  return !!localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function getAdminUser(): AdminUserInfo | null {
  return getStoredUser();
}

// Force refresh user info from DB and update localStorage
export async function refreshAdminUser(): Promise<void> {
  const user = getStoredUser();
  if (!user) return;
  const { supabase: sb } = await import('@/lib/supabase');
  const { data } = await sb
    .from('admin_users')
    .select('id, username, display_name, role, is_active')
    .eq('username', user.username)
    .maybeSingle();
  if (data) {
    const updated: AdminUserInfo = {
      id: data.id,
      username: data.username,
      display_name: data.display_name,
      role: data.role,
    };
    localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(updated));
  }
}

export async function adminLogout(): Promise<void> {
  const user = getStoredUser();
  if (user) {
    await supabase.from('admin_operation_logs').insert({
      operator_username: user.username,
      operator_display_name: user.display_name,
      operator_role: user.role,
      module: '系统',
      action: '退出',
      target_name: '',
      detail: '退出后台管理系统',
    });
  }
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
}
